/**
 * País efectivo para checkout VIP / pricing en DualCTA y MuxVideoPlayerWithCTA.
 *
 * Operador en Argentina prueba intl sin debug del Worker:
 *   /video-landing?checkout_country=CO
 *   /invitacion-premium?checkout_country=US
 *
 * Prioridad:
 *   1) checkout_country (URL) — preview explícito del operador
 *   2) Admin GeoSim activo → país simulado (Worker / session) — **antes** de
 *      test_country en localStorage (evita que un ?country=MX viejo pise AR del
 *      toolbar y oculte el logo Mercado Pago / gateway MP)
 *   3) Worker country (edge inject) — gana sobre test_country stale
 *   4) test_country (localStorage) — solo si el Worker no resolvió país
 *   5) detectCountryCode
 */

import { detectCountryCode } from "@/lib/countryDetection";
import { GEO_SIM_SESSION_KEY } from "@/lib/geoSim/constants";
import { normalizeWorkerCountry } from "@/lib/serverTemplateDetection";

const CHECKOUT_COUNTRY_PARAM = "checkout_country";

function readUrlCheckoutCountry(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search)
    .get(CHECKOUT_COUNTRY_PARAM)
    ?.trim()
    .toUpperCase();
  return raw && /^[A-Z]{2}$/.test(raw) ? raw : null;
}

function readTestCountry(): string | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem("test_country")?.trim().toUpperCase();
  return raw && /^[A-Z]{2}$/.test(raw) ? raw : null;
}

function readAdminGeoSimCountry(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const injected = (
      window as unknown as {
        __TEMPLATE_CONFIG__?: {
          adminGeoSim?: { country?: string };
          country?: string;
        };
      }
    ).__TEMPLATE_CONFIG__;
    const fromEdge = String(injected?.adminGeoSim?.country || "")
      .trim()
      .toUpperCase();
    if (/^[A-Z]{2}$/.test(fromEdge)) return fromEdge;

    // Cookie/session toolbar (hard-nav puede no reinyectar aún)
    const raw = sessionStorage.getItem(GEO_SIM_SESSION_KEY);
    if (raw) {
      const o = JSON.parse(raw) as { country?: string };
      const cc = String(o.country || "")
        .trim()
        .toUpperCase();
      if (/^[A-Z]{2}$/.test(cc)) return cc;
    }

    // geo_sim=1 en URL + country Worker
    const params = new URLSearchParams(window.location.search);
    if (params.get("geo_sim") === "1" && injected?.adminGeoSim) {
      const w = String(injected.country || "")
        .trim()
        .toUpperCase();
      if (/^[A-Z]{2}$/.test(w)) return w;
    }
  } catch {
    /* private mode */
  }
  return null;
}

export function resolveCheckoutCountryCode(
  workerCountry?: string | null,
): string {
  const fromUrl = readUrlCheckoutCountry();
  if (fromUrl) return fromUrl;

  // GeoSim activo: el país simulado manda (MP logo AR / gateway MP).
  const fromGeoSim = readAdminGeoSimCountry();
  if (fromGeoSim) return fromGeoSim;

  // Worker / edge inject gana sobre test_country stale en localStorage.
  // Bug: operador con test_country=US activaba Comunidad CABA y DualCTA
  // mostraba Stripe aunque el edge inyectara AR (VIP debe ser MercadoPago).
  const normalized = normalizeWorkerCountry(workerCountry);
  if (normalized && normalized !== "UNKNOWN") return normalized;

  const fromTest = readTestCountry();
  if (fromTest) return fromTest;

  const detected = detectCountryCode();
  if (detected) {
    const n = normalizeWorkerCountry(detected);
    if (n && n !== "UNKNOWN") return n;
  }

  return "UNKNOWN";
}

/**
 * True si el visitante (o simulación) es Argentina — trust badge MP en Mux/DualCTA.
 *
 * Si `effectiveCountry` es un ISO2 explícito no-AR (GeoSim MX, checkout_country=US),
 * gana sobre workerCountry real (operador en AR simulando exterior) — evita badge MP
 * en exterior cuando el edge aún reporta AR o la sesión real es AR.
 */
export function isArgentinaPaymentContext(
  workerCountry?: string | null,
  effectiveCountry?: string | null,
): boolean {
  const eff = String(effectiveCountry || "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{2}$/.test(eff) && eff !== "AR") return false;
  if (eff === "AR") return true;
  if (String(workerCountry || "").toUpperCase() === "AR") return true;
  if (readAdminGeoSimCountry() === "AR") return true;
  return false;
}

/** Rutas donde el template no depende del Worker — no recargar por ?country=. */
export const CHECKOUT_PREVIEW_PATHS = [
  "/video-landing",
  "/invitacion-premium",
  "/premium-invite",
] as const;

export function isCheckoutPreviewPath(pathname: string): boolean {
  return CHECKOUT_PREVIEW_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}